/**
 * Profile header: avatar, name, member code, type badge (in the type's colour), membership status,
 * age group, member since, a completeness meter and the login / Planning Centre / Anatomy badges.
 */
import type { ReactNode } from 'react';
import { IconCalendarStats, IconLink, IconUserCheck, IconUserOff, IconHeartbeat, IconPlugConnected, IconTrash, IconGitMerge } from '@tabler/icons-react';
import { Card, Group, Stack, Text, Badge, Avatar, Progress, Tooltip, Title } from '@/components/ui';
import type { MemberRecord } from './memberFormat';
import { ageFromDob, ageGroupFor, formatStamp, initialsOf, memberFullName, membershipStatusFor } from './memberFormat';

export interface Completeness {
  percent: number;
  missing: string[];
}

interface Props {
  member: MemberRecord;
  mode: 'self' | 'admin';
  typeName?: string | null;
  typeColor?: string | null;
  completeness: Completeness | null;
  actions?: ReactNode;
  extraBadges?: ReactNode;
  /** In-app help page key of the host page: adds the "{key}.header" and "{key}.completeness" tour anchors. */
  helpPageKey?: string;
}

/** Mantine colour names ("blue") go to the color prop; hex colours are tinted by hand. */
function namedColor(color?: string | null) {
  return color && !color.startsWith('#') ? color : undefined;
}

function tint(color?: string | null) {
  if (!color) return undefined;
  if (!color.startsWith('#')) return undefined;
  return { backgroundColor: `${color}22`, color, borderColor: `${color}55` };
}

export function MemberProfileHeader({ member, mode, typeName, typeColor, completeness, actions, extraBadges, helpPageKey }: Props) {
  const name = memberFullName(member) || member.email || `Member ${member.memberID}`;
  const status = membershipStatusFor(member);
  const age = member.age ?? ageFromDob(member.dateOfBirth);
  const group = member.ageGroup && member.ageGroup !== 'Unknown'
    ? { label: member.ageGroup, color: member.ageGroupColor ?? '#9E9E9E' }
    : ageGroupFor(age);
  const pct = completeness ? Math.round(completeness.percent) : null;
  const meterColor = pct === null ? 'gray' : pct >= 100 ? 'green' : pct >= 60 ? 'blue' : 'orange';

  return (
    <Card withBorder padding="lg" radius="md" data-help-id={helpPageKey ? `${helpPageKey}.header` : undefined}>
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="lg">
        <Group gap="lg" align="flex-start" wrap="nowrap" style={{ flex: 1, minWidth: 260 }}>
          <Avatar src={member.avatar || undefined} size={88} radius="xl" color="initials" alt={name}>{initialsOf(name)}</Avatar>
          <Stack gap={6} style={{ minWidth: 0 }}>
            <div>
              <Title order={2} style={{ wordBreak: 'break-word', lineHeight: 1.2 }}>{name}</Title>
              <Text size="sm" c="dimmed">
                {[member.memberCode ? `Member code ${member.memberCode}` : null, mode === 'admin' ? `ID ${member.memberID}` : null, age != null ? `${age} years old` : null]
                  .filter(Boolean).join(' · ') || 'No member code yet'}
              </Text>
            </div>
            <Group gap={6} wrap="wrap">
              {(typeName || member.typeName) && (
                <Badge variant="light" color={namedColor(typeColor ?? member.typeColor)} style={tint(typeColor ?? member.typeColor)}>{typeName || member.typeName}</Badge>
              )}
              <Badge variant="light" color={status.color} leftSection={member.isActive ? <IconUserCheck size={12} /> : <IconUserOff size={12} />}>
                {status.label}
              </Badge>
              {group && <Badge variant="light" style={tint(group.color)}>{group.label}</Badge>}
              {mode === 'admin' && (
                member.userId
                  ? <Badge variant="light" color="indigo" leftSection={<IconLink size={12} />}>Has a login</Badge>
                  : <Badge variant="outline" color="gray">No login</Badge>
              )}
              {member.planningCentrePersonId && (
                <Tooltip label={`Planning Centre person ${member.planningCentrePersonId}`}>
                  <Badge variant="light" color="violet" leftSection={<IconPlugConnected size={12} />}>Planning Centre</Badge>
                </Tooltip>
              )}
              {member.anatomyUserId ? (
                <Tooltip label={`Anatomy user ${member.anatomyUserId}`}>
                  <Badge variant="light" color="red" leftSection={<IconHeartbeat size={12} />}>Anatomy</Badge>
                </Tooltip>
              ) : null}
              {member.isDeleted && (
                <Badge variant="filled" color="red" leftSection={member.mergedIntoMemberID ? <IconGitMerge size={12} /> : <IconTrash size={12} />}>
                  {member.mergedIntoMemberID ? `Merged into #${member.mergedIntoMemberID}` : 'Deleted'}
                </Badge>
              )}
              {extraBadges}
            </Group>
            <Group gap={6}>
              <IconCalendarStats size={14} style={{ opacity: 0.6 }} />
              <Text size="xs" c="dimmed">On record since {formatStamp(member.createdDateTime)}</Text>
            </Group>
          </Stack>
        </Group>

        <Stack gap="sm" align="flex-end" style={{ minWidth: 220 }}>
          {actions}
          {completeness && (
            <Tooltip
              multiline
              w={260}
              label={completeness.missing.length ? `Still missing: ${completeness.missing.join(', ')}` : 'Everything is filled in'}
            >
              <Stack gap={4} style={{ width: 220 }} data-help-id={helpPageKey ? `${helpPageKey}.completeness` : undefined}>
                <Group justify="space-between">
                  <Text size="xs" c="dimmed">Profile complete</Text>
                  <Text size="xs" fw={600}>{pct}%</Text>
                </Group>
                <Progress value={pct ?? 0} color={meterColor} size="md" radius="xl" aria-label="Profile completeness" />
                {completeness.missing.length > 0 && (
                  <Text size="xs" c="dimmed" lineClamp={1}>Missing: {completeness.missing.join(', ')}</Text>
                )}
              </Stack>
            </Tooltip>
          )}
        </Stack>
      </Group>
    </Card>
  );
}
